import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { NextSearchParams } from '@/types/general';
import { useInteractifyParams } from '@/features/interactify/utils/use-vid-params';
import InteractifyVideoModal from '@/features/interactify/video-modal';
import { useMobileDetect } from '@/features/mobile-detect-context/mobile-detect-provider';
import { PUBLIC_IS_BOT } from '@/app/_constants/search-engines';
import { cn } from '@/utils/cn';
import ListSkeleton from './list-skeleton';
import NoResult from './no-result';
import SingleShort from './single-short';
import { ROW_GROUP_SIZE } from '../constants';
import useGetVideos from '../utils/use-get-videos';
import { useResponsiveHeight } from '../utils/use-responsive-height';
interface Props {
  searchParams: NextSearchParams;
}
const ListWrapper = ({
  searchParams
}: Props) => {
  const {
    inView,
    ref
  } = useInView();
  const {
    addInteractifyParam,
    removeInteractifyParam
  } = useInteractifyParams();
  const {
    isLoading,
    videos,
    fetchNextPage,
    isFetchingNextPage,
    isFetching
  } = useGetVideos({
    searchParams
  });
  const {
    isMobile
  } = useMobileDetect();
  const {
    containerRef,
    rowHeight
  } = useResponsiveHeight({
    debounceMs: 16
  });
  const handleClick = (slug: string) => {
    addInteractifyParam({
      slug,
      isPushingNewRoute: true
    });
  };
  const rowCount = Math.ceil(videos.length / ROW_GROUP_SIZE);
  const rowVirtualizer = useWindowVirtualizer({
    count: rowCount,
    estimateSize: () => rowHeight,
    overscan: PUBLIC_IS_BOT ? rowCount : 1,
    initialRect: {
      width: 900,
      height: PUBLIC_IS_BOT ? rowHeight * rowCount : rowHeight
    },
    enabled: rowHeight !== 0
  });
  useEffect(() => {
    inView && fetchNextPage();
  }, [inView, fetchNextPage]);
  useEffect(() => {
    if (rowHeight > 0) {
      rowVirtualizer.measure();
    }
  }, [rowHeight, rowVirtualizer]);
  const virtualRows = rowVirtualizer.getVirtualItems();
  const showNoResult = !isLoading && !videos.length;
  return <>
            {!isLoading && <InteractifyVideoModal closeHandler={removeInteractifyParam} searchParams={searchParams} fetchNextPage={fetchNextPage} />}

            <div ref={containerRef} className={cn({
      'flex grow': showNoResult
    })}>
                {isLoading && <ListSkeleton />}

                {showNoResult && <NoResult />}

                {!isLoading && <div style={{
        height: `${rowVirtualizer.getTotalSize()}px`,
        position: 'relative'
      }}>
                        {virtualRows.map(row => {
          const rowStartIndex = row.index * ROW_GROUP_SIZE;
          const rowEndIndex = Math.min(rowStartIndex + ROW_GROUP_SIZE, videos.length);
          const items = videos.slice(rowStartIndex, rowEndIndex);
          return <div key={row.index} className='absolute left-0 top-0 w-full' style={{
            transform: `translateY(${row.start}px)`,
            height: rowHeight - (isMobile ? 4 : 10)
          }}>
                                    <div className={cn('grid h-full grid-cols-3', {
              'gap-1': isMobile,
              'gap-2.5': !isMobile
            })}>
                                        {items.map((item, columnIndex) => {
                const indexInAllItems = rowStartIndex + columnIndex;
                const isVideo = indexInAllItems % 10 === 0 || indexInAllItems % 10 === 7;
                return <SingleShort key={item.slug} item={item} isVideo={isVideo} clickHandler={() => handleClick(item.slug || '')} />;
              })}
                                    </div>
                                </div>;
        })}
                    </div>}
            </div>

            {!isFetching && !showNoResult && <div ref={ref} className='h-1 w-full' />}

            {!!isFetchingNextPage && <ListSkeleton />}
        </>;
};
export default ListWrapper;